import React, { useState } from "react";
import {
	Box,
	Chip,
	CircularProgress,
	IconButton,
	Tooltip,
	Typography
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { formatDistanceToNow, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "react-toastify";
import api from "../../services/api";

const COLOR_BY_RATING = {
	GREEN: { bg: "#28a745", label: "Verde", tip: "Excelente — clientes recebem bem suas mensagens." },
	YELLOW: { bg: "#ffc107", label: "Amarelo", tip: "Atenção: número de bloqueios acima da média. Reduza a frequência de envio nos próximos 7 dias." },
	RED: { bg: "#dc3545", label: "Vermelho", tip: "Risco: Meta pode limitar ou bloquear este número. Reduza disparos imediatamente." },
	UNKNOWN: { bg: "#6c757d", label: "Desconhecido", tip: "Não foi possível ler da Meta (token expirado ou instância desconectada)." }
};

const SEND_COLOR_BY_STATUS = {
	AVAILABLE: "#28a745",
	LIMITED: "#ffc107",
	BLOCKED: "#dc3545"
};

const MetaHealthBadge = ({ whatsApp, compact = false }) => {
	const [refreshing, setRefreshing] = useState(false);

	if (!whatsApp || whatsApp.type !== "waba") return null;

	const rating = whatsApp.metaQualityRating || "UNKNOWN";
	const meta = COLOR_BY_RATING[rating] || COLOR_BY_RATING.UNKNOWN;
	const sendStatus = whatsApp.metaCanSendMessage;
	const lastChecked = whatsApp.metaLastCheckedAt
		? formatDistanceToNow(parseISO(whatsApp.metaLastCheckedAt), {
				addSuffix: true,
				locale: ptBR
		  })
		: "nunca";

	const handleRefresh = async (e) => {
		e?.stopPropagation?.();
		if (refreshing) return;
		setRefreshing(true);
		try {
			await api.post(`/whatsapp/${whatsApp.id}/refresh-meta-health`);
			toast.success("Saúde Meta atualizada", { position: "top-right" });
			setTimeout(() => window.location.reload(), 600);
		} catch (err) {
			toast.error("Falha ao atualizar saúde Meta");
		} finally {
			setRefreshing(false);
		}
	};

	const tooltipContent = (
		<Box sx={{ p: 0.5 }}>
			<Typography variant="subtitle2" fontWeight={600} gutterBottom>
				Saúde Meta — {meta.label}
			</Typography>
			<Typography variant="body2" sx={{ mb: 1 }}>
				{meta.tip}
			</Typography>
			{sendStatus && (
				<Typography variant="caption" display="block">
					Envio: <strong style={{ color: SEND_COLOR_BY_STATUS[sendStatus] || "#666" }}>{sendStatus}</strong>
				</Typography>
			)}
			{whatsApp.metaThroughputLevel && (
				<Typography variant="caption" display="block">
					Throughput: <strong>{whatsApp.metaThroughputLevel === "STANDARD" ? "80 msg/s (padrão)" : whatsApp.metaThroughputLevel}</strong>
				</Typography>
			)}
			<Typography variant="caption" display="block" color="text.secondary">
				Atualizado {lastChecked}
			</Typography>
		</Box>
	);

	if (compact) {
		return (
			<Tooltip title={tooltipContent} arrow placement="top">
				<Box
					sx={{
						display: "inline-flex",
						alignItems: "center",
						gap: 0.5
					}}
				>
					<Box
						sx={{
							width: 12,
							height: 12,
							borderRadius: "50%",
							bgcolor: meta.bg,
							boxShadow: `0 0 0 2px ${meta.bg}33`
						}}
					/>
				</Box>
			</Tooltip>
		);
	}

	return (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
			<Tooltip title={tooltipContent} arrow placement="top">
				<Chip
					size="small"
					label={`Saúde: ${meta.label}`}
					sx={{
						bgcolor: meta.bg,
						color: "#fff",
						fontWeight: 600,
						"& .MuiChip-label": { px: 1 }
					}}
				/>
			</Tooltip>
			<Tooltip title="Atualizar saúde Meta agora">
				<IconButton size="small" onClick={handleRefresh} disabled={refreshing}>
					{refreshing ? <CircularProgress size={14} /> : <RefreshIcon fontSize="small" />}
				</IconButton>
			</Tooltip>
		</Box>
	);
};

export default MetaHealthBadge;
